/**
 * @fileoverview Конвертация даты в текстовое представление (дата прописью).
 * Поддерживает русский (ru) и казахский (kz) языки. Для en возвращает
 * отформатированную строку (не прописью).
 *
 * Переиспользует numberToWords для генерации количественных числительных,
 * после чего последнее слово заменяется на порядковую форму (родительный падеж).
 */

import { numberToWords } from "./numberToWords";

// Месяцы в родительном падеже (ru) — "25 декабря".
const RU_MONTHS_GEN = [
  "января",
  "февраля",
  "марта",
  "апреля",
  "мая",
  "июня",
  "июля",
  "августа",
  "сентября",
  "октября",
  "ноября",
  "декабря",
];

const KZ_MONTHS = [
  "қаңтар",
  "ақпан",
  "наурыз",
  "сәуір",
  "мамыр",
  "маусым",
  "шілде",
  "тамыз",
  "қыркүйек",
  "қазан",
  "қараша",
  "желтоқсан",
];

const EN_MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

// Последнее слово количественного числительного -> порядковое (род. падеж), ru.
const RU_ORDINAL_GENITIVE = {
  один: "первого",
  два: "второго",
  две: "второго",
  три: "третьего",
  четыре: "четвёртого",
  пять: "пятого",
  шесть: "шестого",
  семь: "седьмого",
  восемь: "восьмого",
  девять: "девятого",
  десять: "десятого",
  одиннадцать: "одиннадцатого",
  двенадцать: "двенадцатого",
  тринадцать: "тринадцатого",
  четырнадцать: "четырнадцатого",
  пятнадцать: "пятнадцатого",
  шестнадцать: "шестнадцатого",
  семнадцать: "семнадцатого",
  восемнадцать: "восемнадцатого",
  девятнадцать: "девятнадцатого",
  двадцать: "двадцатого",
  тридцать: "тридцатого",
  сорок: "сорокового",
  пятьдесят: "пятидесятого",
  шестьдесят: "шестидесятого",
  семьдесят: "семидесятого",
  восемьдесят: "восьмидесятого",
  девяносто: "девяностого",
  сто: "сотого",
  двести: "двухсотого",
  триста: "трёхсотого",
  четыреста: "четырёхсотого",
  пятьсот: "пятисотого",
  шестьсот: "шестисотого",
  семьсот: "семисотого",
  восемьсот: "восьмисотого",
  девятьсот: "девятисотого",
  тысяча: "тысячного",
};

// Префикс родительного падежа числительного для круглых тысяч (ru): 2000 -> "двухтысячного".
const RU_THOUSAND_PREFIX = {
  2: "двух",
  3: "трёх",
  4: "четырёх",
  5: "пяти",
  6: "шести",
  7: "семи",
  8: "восьми",
  9: "девяти",
};

// Последнее слово количественного числительного -> порядковое, kz.
const KZ_ORDINAL = {
  бір: "бірінші",
  екі: "екінші",
  үш: "үшінші",
  төрт: "төртінші",
  бес: "бесінші",
  алты: "алтыншы",
  жеті: "жетінші",
  сегіз: "сегізінші",
  тоғыз: "тоғызыншы",
  он: "оныншы",
  жиырма: "жиырмасыншы",
  отыз: "отызыншы",
  қырық: "қырқыншы",
  елу: "елуінші",
  алпыс: "алпысыншы",
  жетпіс: "жетпісінші",
  сексен: "сексенінші",
  тоқсан: "тоқсаныншы",
  жүз: "жүзінші",
  мың: "мыңыншы",
};

/**
 * Заменяет последнее слово количественного числительного на порядковую форму.
 * @param {number} value
 * @param {"ru"|"kz"} lang
 * @returns {string}
 */
function ordinalFromCardinal(value, lang) {
  const cardinal = numberToWords(value, lang);
  if (!cardinal) return "";

  const words = cardinal.split(" ");
  const last = words[words.length - 1];
  const map = lang === "kz" ? KZ_ORDINAL : RU_ORDINAL_GENITIVE;
  const ordinal = map[last];

  if (ordinal) {
    words[words.length - 1] = ordinal;
    return words.join(" ");
  }

  return cardinal;
}

/**
 * Порядковое числительное года в родительном падеже (ru), с обработкой круглых тысяч.
 * @param {number} year
 * @returns {string}
 */
function ruYearOrdinal(year) {
  if (year % 1000 === 0) {
    const thousands = year / 1000;
    if (thousands === 1) return "тысячного";
    const prefix = RU_THOUSAND_PREFIX[thousands];
    if (prefix) return `${prefix}тысячного`;
  }
  return ordinalFromCardinal(year, "ru");
}

/**
 * Приводит входное значение к { year, month (1-12), day }.
 * Принимает объект { year, month, day }, Date или строку (DD.MM.YYYY, YYYY-MM-DD).
 * @returns {{ year: number, month: number, day: number } | null}
 */
function parseDateValue(value) {
  if (value === null || value === undefined || value === "") return null;

  if (typeof value === "object" && !(value instanceof Date)) {
    const { year, month, day } = value;
    if (year && month && day) {
      return { year: Number(year), month: Number(month), day: Number(day) };
    }
    return null;
  }

  if (value instanceof Date) {
    if (isNaN(value.getTime())) return null;
    return {
      year: value.getFullYear(),
      month: value.getMonth() + 1,
      day: value.getDate(),
    };
  }

  const str = String(value).trim();

  // DD.MM.YYYY
  const dotMatch = str.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
  if (dotMatch) {
    return {
      day: Number(dotMatch[1]),
      month: Number(dotMatch[2]),
      year: Number(dotMatch[3]),
    };
  }

  // YYYY-MM-DD
  const dashMatch = str.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (dashMatch) {
    return {
      year: Number(dashMatch[1]),
      month: Number(dashMatch[2]),
      day: Number(dashMatch[3]),
    };
  }

  return null;
}

/**
 * Конвертирует дату в текстовое представление (прописью для ru/kz).
 * @param {Object|Date|string} value - Дата ({year,month,day}, Date или строка)
 * @param {"ru"|"kz"|"en"} lang - Язык
 * @returns {string} Дата прописью / форматированная дата
 *
 * @example
 * dateToWords({ year: 2024, month: 12, day: 25 }, "ru")
 * // "двадцать пятого декабря две тысячи двадцать четвёртого года"
 */
export function dateToWords(value, lang = "ru") {
  const parsed = parseDateValue(value);
  if (!parsed) return "";

  const { year, month, day } = parsed;
  const monthIndex = month - 1;
  if (monthIndex < 0 || monthIndex > 11) return "";

  if (lang === "kz") {
    const yearOrd = ordinalFromCardinal(year, "kz");
    const dayOrd = ordinalFromCardinal(day, "kz");
    return `${yearOrd} жылғы ${dayOrd} ${KZ_MONTHS[monthIndex]}`;
  }

  if (lang === "en") {
    return `${day} ${EN_MONTHS[monthIndex]} ${year}`;
  }

  // ru — по умолчанию
  const dayOrd = ordinalFromCardinal(day, "ru");
  const yearOrd = ruYearOrdinal(year);
  return `${dayOrd} ${RU_MONTHS_GEN[monthIndex]} ${yearOrd} года`;
}

/**
 * Форматирует дату как DD.MM.YYYY с ведущими нулями. Язык-нейтрально.
 * @param {Object|Date|string} value - Дата ({year,month,day}, Date или строка)
 * @returns {string}
 *
 * @example
 * dateToNumeric({ year: 2027, month: 6, day: 5 }) // "05.06.2027"
 */
export function dateToNumeric(value) {
  const parsed = parseDateValue(value);
  if (!parsed) return "";

  const { year, month, day } = parsed;
  const dd = String(day).padStart(2, "0");
  const mm = String(month).padStart(2, "0");
  return `${dd}.${mm}.${year}`;
}
